import { Callout, Card, Colors, Icon, Text, TextInput } from '@amelio/ui-library';
import { useEffect, useId, useState, type Ref } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { showWarningToast } from '@/plugins/Toast';

const MIN_EVALUATOR_COUNT = 1;
const MAX_EVALUATOR_COUNT = 8;
const DEFAULT_EVALUATOR_COUNT = 5;

interface EvaluatorCountInputProps {
    value?: number;
    errorMessage?: string;
    inputRef?: Ref<HTMLInputElement>;
    isReadOnly?: boolean;
    onChange: (value: number) => void;
    dataTestId?: string;
}

const EvaluatorCountInput = ({
    value = DEFAULT_EVALUATOR_COUNT,
    errorMessage,
    inputRef,
    isReadOnly = false,
    onChange,
    dataTestId = 'evaluator-count-input',
}: EvaluatorCountInputProps) => {
    const { t } = useAmelioTranslations();
    const [inputValue, setInputValue] = useState(String(value));
    const errorId = useId();

    useEffect(() => {
        setInputValue(String(value));
    }, [value]);

    const handleChange = (newValue: string) => {
        setInputValue(newValue);

        const numValue = Number(newValue);
        if (
            !isNaN(numValue) &&
            numValue >= MIN_EVALUATOR_COUNT &&
            numValue <= MAX_EVALUATOR_COUNT
        ) {
            onChange(numValue);
        }
    };

    const handleBlur = () => {
        const numValue = Number(inputValue);
        if (isNaN(numValue) || numValue < MIN_EVALUATOR_COUNT || inputValue === '') {
            setInputValue(String(MIN_EVALUATOR_COUNT));
            onChange(MIN_EVALUATOR_COUNT);
        } else if (numValue > MAX_EVALUATOR_COUNT) {
            showWarningToast({
                message: t('cycleEvaluationSelectEvaluators.evaluatorSelection.valueCorrected', {
                    maxCount: MAX_EVALUATOR_COUNT,
                }),
            });
            setInputValue(String(MAX_EVALUATOR_COUNT));
            onChange(MAX_EVALUATOR_COUNT);
        }
    };

    return (
        <Card
            className={'mt-9 max-md:p-4 md:p-10'}
            padding={'none'}
            bgColor={'bg-white'}
            borderRadius={'2xl'}
            dataTestId={dataTestId}
        >
            <div className={'flex items-start gap-6'}>
                <Icon
                    name={'users-people'}
                    size={'xl'}
                    color={Colors.BASE}
                />

                <div className={'flex flex-col'}>
                    <Text
                        weight={'semibold'}
                        size={'default'}
                    >
                        {t('cycleEvaluationSelectEvaluators.evaluatorSelection.question')}
                    </Text>

                    <div className={'mt-6'}>
                        <TextInput
                            ref={inputRef}
                            type={'number'}
                            className={'w-32'}
                            size={'mdPlus'}
                            textSize={'sm'}
                            bordered
                            placeholder={String(DEFAULT_EVALUATOR_COUNT)}
                            value={inputValue}
                            readOnly={isReadOnly}
                            ariaDescribedBy={errorMessage ? errorId : undefined}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            dataTestId={`${dataTestId}-field`}
                        />

                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'mt-4 text-xs'}
                        >
                            {t('cycleEvaluationSelectEvaluators.evaluatorSelection.recommendation')}
                        </Text>

                        {errorMessage && (
                            <div
                                id={errorId}
                                className={'mt-4'}
                            >
                                <Callout tone={'warning'}>{errorMessage}</Callout>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
};

export { EvaluatorCountInput };
